import { useState } from "react";
import { X, Send, Radio, Target, Lightbulb } from "lucide-react";
import { toast } from "sonner";
import { http, fmtDate, errMsg, tname } from "@/lib/api";
import { ProbBar } from "@/components/MatchCard";

const Metric = ({ label, value, testid }) => (
  <div className="border border-slate-800 rounded-lg p-4">
    <div className="text-[11px] uppercase tracking-wider text-slate-400">{label}</div>
    <div data-testid={testid} className="font-display text-2xl font-bold mt-1">{value}</div>
  </div>
);

const EXTRA = [["primo_tempo", "1° tempo"], ["parziale_finale", "Parziale/Finale"], ["over_1_5", "O/U 1.5"], ["over_3_5", "O/U 3.5"],
  ["multigol", "Multigol"], ["gol_casa", "Gol casa"], ["gol_ospite", "Gol ospite"], ["combo", "Combo"]];

export default function PredictionDialog({ match: m, code, onClose, readOnly = false }) {
  const [busy, setBusy] = useState("");
  const p = m.prediction?.data;
  const mark = (k) => m.evaluation?.[k] ?? null;
  if (!p) return null;

  const send = async (target) => {
    setBusy(target);
    try {
      const r = await http.post(`/telegram/send/${m.id}`, null, { params: { target, competition: code } });
      toast.success(target === "channel" ? "Pubblicato sul canale Telegram" : `Inviato a ${r.data.sent} iscritti`);
    } catch (e) { toast.error(errMsg(e)); }
    setBusy("");
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm grid place-items-center p-4" onClick={onClose}>
      <div data-testid="ai-prediction-modal" className="panel pitch-lines w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6 sm:p-8 fade-up" onClick={(e) => e.stopPropagation()}>
        <button data-testid="ai-prediction-modal-close-btn" onClick={onClose} className="absolute top-4 right-4 text-slate-400 hover:text-white transition-colors"><X size={20} /></button>
        <span className="font-mono text-xs text-[var(--gold)] tracking-widest">PRONOSTICO AI · {m.prediction.competition_name} · {m.prediction.round_label} · {fmtDate(m.utcDate)}</span>
        <div className="flex items-center gap-4 mt-3">
          <img src={m.homeTeam.crest} alt="" className="w-12 h-12 object-contain" />
          <h3 className="font-display text-3xl sm:text-4xl font-extrabold uppercase flex-1">{tname(m.homeTeam)} <span className="text-slate-500">vs</span> {tname(m.awayTeam)}</h3>
          <img src={m.awayTeam.crest} alt="" className="w-12 h-12 object-contain" />
        </div>
        {m.status === "FINISHED" && (
          <div data-testid="modal-final-result" className="mt-4 inline-flex items-center gap-2 rounded-md border border-[var(--win)]/40 bg-[var(--win)]/10 px-3 py-1.5 text-sm text-[var(--win)] font-semibold">
            ✓ Già giocata · Risultato finale {m.score.fullTime.home}-{m.score.fullTime.away}
          </div>
        )}

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-6">
          <Metric label="Esito" value={<span className="text-[var(--gold)]">{p.esito}</span>} testid="modal-esito" />
          <Metric label="Risultato esatto" value={p.risultato_esatto} testid="modal-exact" />
          <Metric label="Goal / No Goal" value={p.goal_nogoal} testid="modal-gg" />
          <Metric label="Over / Under" value={p.over_under} testid="modal-ou" />
        </div>

        <div className="mt-6 space-y-2">
          <ProbBar p={p} />
          <div className="flex justify-between text-xs font-mono text-slate-400">
            <span className="text-[var(--win)]">1 · {p.prob_1}%</span><span className="text-[var(--draw)]">X · {p.prob_x}%</span><span className="text-[var(--loss)]">2 · {p.prob_2}%</span>
          </div>
        </div>

        <div className="mt-6">
          <div className="flex justify-between text-xs uppercase tracking-wider text-slate-400"><span>Confidenza AI</span><span data-testid="modal-confidence" className="text-white font-mono">{p.confidenza}%</span></div>
          <div className="h-2 bg-slate-800 rounded-full mt-2"><div className="h-full rounded-full bg-[var(--gold)]" style={{ width: `${p.confidenza}%` }} /></div>
        </div>

        <div className="mt-6 border-l-2 border-[var(--gold)] pl-4">
          <div className="flex items-center gap-2 text-sm font-semibold"><Lightbulb size={16} className="text-[var(--gold)]" /> Consiglio: {p.consiglio}</div>
          <p data-testid="modal-analysis" className="text-slate-300 mt-2 leading-relaxed">{p.analisi}</p>
        </div>

        {p.mercati && (
          <div data-testid="modal-extra-markets" className="mt-6">
            <div className="text-[11px] uppercase tracking-wider text-slate-400 mb-2">Altri mercati</div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {EXTRA.map(([k, label]) => p.mercati[k]?.scelta && (
                <div key={k} data-testid={`market-${k}`} className={`rounded-lg border p-3 ${mark(k) === true ? "border-[var(--win)]/50 bg-[var(--win)]/10" : mark(k) === false ? "border-[var(--loss)]/50 bg-[var(--loss)]/10" : "border-slate-800"}`}>
                  <div className="text-[10px] uppercase tracking-wider text-slate-400 flex justify-between">{label}{mark(k) === true ? <span className="text-[var(--win)]">✓</span> : mark(k) === false ? <span className="text-[var(--loss)]">✗</span> : null}</div>
                  <div className="font-display text-lg font-bold leading-tight mt-1">{p.mercati[k].scelta}</div>
                  <div className="font-mono text-[11px] text-slate-500">{p.mercati[k].prob}%</div>
                </div>
              ))}
            </div>
          </div>
        )}

        <ul className="mt-5 space-y-2">
          {p.fattori_chiave?.map((f, i) => (
            <li key={i} className="flex gap-2 text-sm text-slate-300"><Target size={14} className="mt-1 text-[var(--tg)] shrink-0" />{f}</li>
          ))}
        </ul>

        {!readOnly && <div className="flex flex-wrap gap-3 mt-8">
          <button data-testid="publish-to-telegram-modal-btn" disabled={!!busy} onClick={() => send("channel")} className="btn btn-tg"><Radio size={16} /> {busy === "channel" ? "Pubblicazione..." : "Pubblica sul Canale"}</button>
          <button data-testid="send-subscribers-modal-btn" disabled={!!busy} onClick={() => send("subscribers")} className="btn btn-ghost"><Send size={16} /> {busy === "subscribers" ? "Invio..." : "Invia agli iscritti del bot"}</button>
        </div>}
      </div>
    </div>
  );
}
